import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { useNavigate } from 'react-router';
import graphql from 'babel-plugin-relay/macro';

import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import ColumnEditor from 'components/ColumnEditor';
import RoomNameWithCapacity from 'components/RoomNameWithCapacity';

import { useDialog } from 'components/Dialog';
import { useMenu } from 'components/IconButtonMenu';
import { useErrorHandler } from 'lib';

import { RoomRatesEditorRoomSettings_package$key } from '__generated__/RoomRatesEditorRoomSettings_package.graphql';
import { RoomRatesEditorRoomSettingsMutation } from '__generated__/RoomRatesEditorRoomSettingsMutation.graphql';

type Props = {
  package: RoomRatesEditorRoomSettings_package$key;
};

const filter = (item: { nameString: string | null }, search: string) =>
  item.nameString?.toLowerCase().includes(search) ?? false;

const RoomRatesEditorRoomSettings: FC<Props> = props => {
  const { close: closeDialog } = useDialog();
  const { close: closeMenu } = useMenu();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();

  const { id, hotel, rooms } = useFragment(
    graphql`
      fragment RoomRatesEditorRoomSettings_package on Package {
        id

        hotel {
          id

          rooms(first: 2147483647) {
            edges {
              node {
                id
                nameString

                ...RoomNameWithCapacity_room
              }
            }
          }
        }

        rooms {
          id
        }
      }
    `,
    props.package
  );

  const [update, updating] =
    useMutation<RoomRatesEditorRoomSettingsMutation>(graphql`
      mutation RoomRatesEditorRoomSettingsMutation(
        $id: ID!
        $input: UpdatePackageInput!
      ) {
        updatePackage(id: $id, input: $input) {
          ...PackageEditAccommodation_package
        }
      }
    `);

  const handleSaveClick = useCallback(
    (values: string[]) => {
      update({
        variables: {
          id,
          input: {
            rooms: values.map((id, position) => ({ id, position })),
          },
        },

        ...errorHandler({
          onCompleted: () => {
            closeDialog();
            closeMenu();
          },
        }),
      });
    },
    [closeDialog, closeMenu, errorHandler, id, update]
  );

  const handleEditRoomsClick = useCallback(() => {
    if (!hotel) return;

    navigate(`/hotels/${hotel.id}#rooms`);
  }, [hotel, navigate]);

  return (
    <ColumnEditor
      title="Kambarių nustatymai"
      noItemsText="Viešbutis neturi nei vieno kambario"
      noItemsAction={
        <Button
          color="inherit"
          size="small"
          onClick={handleEditRoomsClick}
          startIcon={<EditIcon />}>
          Redaguoti kambarius
        </Button>
      }
      loading={updating}
      items={hotel?.rooms?.edges.map(({ node }) => node) ?? []}
      initial={rooms.map(({ id }) => id)}
      filter={filter}
      renderItem={item => <RoomNameWithCapacity room={item} />}
      onClose={closeMenu}
      onSave={handleSaveClick}
    />
  );
};

export default RoomRatesEditorRoomSettings;
